import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useBrand } from '../context/BrandContext';
import { amplifyApi } from '../services/amplifyApi';
import {
  CheckCircle,
  ArrowRight,
  Loader2,
  Star,
  Crown,
  Zap
} from 'lucide-react';

function SubscriptionSuccessPage() {
  const brand = useBrand();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // Give Stripe webhook time to process
    const timer = setTimeout(() => {
      loadSubscriptionStatus();
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const loadSubscriptionStatus = async () => {
    const response = await amplifyApi.getSubscriptionStatus();
    if (response.success) {
      setSubscription(response);
    }
    setLoading(false);
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'pro': return Star;
      case 'enterprise': return Crown;
      default: return Zap;
    }
  };

  const getTierName = (tier) => {
    return tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : 'Pro';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-green-600 mx-auto mb-4" />
          <p className="text-green-700">Setting up your subscription...</p>
        </div>
      </div>
    );
  }

  const TierIcon = getTierIcon(subscription?.tier);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        {/* Success Icon */}
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-12 h-12 text-green-600" />
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-slate-900 mb-2">
          Welcome to {getTierName(subscription?.tier)}!
        </h1>
        <p className="text-slate-600 mb-6">
          Your subscription is now active. You have access to all {getTierName(subscription?.tier)} features.
        </p>

        {/* New Tier Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full mb-8">
          <TierIcon className="w-5 h-5" />
          <span className="font-semibold">{getTierName(subscription?.tier)} Plan Active</span>
        </div>

        {/* What's New Section */}
        <div className="bg-slate-50 rounded-xl p-5 mb-8 text-left">
          <h3 className="font-semibold text-slate-900 mb-3">What's new for you:</h3>
          <ul className="space-y-2 text-sm text-slate-600">
            {subscription?.tier === 'pro' && (
              <>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  10 products per session (was 3)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  50 products per day (was 10)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  500 products per month (was 50)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  20 AI generations per hour (was 5)
                </li>
              </>
            )}
            {subscription?.tier === 'enterprise' && (
              <>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  50 products per session
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  500 products per day
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  10,000 products per month
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  100 AI generations per hour
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  Priority support & API access
                </li>
              </>
            )}
          </ul>
        </div>

        {/* CTA Buttons */}
        <div className="space-y-3">
          <Link
            to={brand?.routes?.studio || '/create'}
            className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Start Creating
            <ArrowRight className="w-5 h-5" />
          </Link>
          <Link
            to="/profile"
            className="block w-full py-3 px-4 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg font-medium transition-colors"
          >
            View Your Profile
          </Link>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionSuccessPage;
